import { CharCounter, Input, Select } from "@/components";
import { useTranslation } from "@/contexts/LanguageContext";
import { getPlatformName, PlatformIcon } from "@/utils/platformIcons";
import {
  ClipboardList,
  Facebook,
  Globe,
  Heart,
  Image as ImageIcon,
  Instagram,
  Layers,
  Megaphone,
  MessageCircle,
  MousePointerClick,
  Phone,
  PhoneCall,
  Search,
  Settings2,
  Sparkles,
  Target,
  UserPlus,
  Video,
} from "lucide-react";
import React from "react";
import { PerformanceGoal, useAds } from "../hooks/AdsContext";
import {
  CAMPAIGN_NAME_LIMITS,
  GOOGLE_ADS_BUSINESS_NAME_LIMIT,
} from "../utils/charLimits";

const PERFORMANCE_GOAL_LABELS: Record<PerformanceGoal, string> = {
  CONVERSATIONS: "Maximizar o número de conversas",
  LANDING_PAGE_VIEWS: "Maximizar visualizações da Página de destino",
  LINK_CLICKS: "Maximizar o número de cliques na ligação",
  REACH: "Maximizar o alcance individual diário",
  QUALITY_CALL: "Maximizar o número de chamadas",
};

const PERFORMANCE_GOAL_GROUPS: { label: string; goals: PerformanceGoal[] }[] = [
  {
    label: "Objetivos de tráfego",
    goals: ["LANDING_PAGE_VIEWS", "LINK_CLICKS"],
  },
  {
    label: "Outros objetivos",
    goals: ["REACH", "CONVERSATIONS", "QUALITY_CALL"],
  },
];

const Step1_GeneralConfig: React.FC = () => {
  const {
    platform,
    setPlatform,
    name,
    setName,
    destinationType,
    setDestinationType,
    performanceGoal,
    setPerformanceGoal,
    googleCampaignType,
    setGoogleCampaignType,
    whatsappPhone,
    setWhatsappPhone,
    whatsappPrefill,
    setWhatsappPrefill,
    whatsappOfficial,
    setWhatsappOfficial,
    metaPlatform,
    setMetaPlatform,
    businessName,
    setBusinessName,
    linkedinObjective,
    setLinkedinObjective,
    chatgptObjective,
    setChatgptObjective,
  } = useAds();
  const { t } = useTranslation();

  return (
    <div className="w-full space-y-4 animate-in fade-in slide-in-from-bottom-3 duration-400">
      {/* Platform Selector Row */}
      <div
        className={`bg-white rounded-2xl p-4 md:p-5 border ${platform === "linkedin" ? "border-[#0077b5]/30 shadow-xs" : "border-purple-200/70 shadow-xs"} space-y-3.5`}
      >
        <div className="flex items-center justify-between border-b pb-3 border-purple-100">
          <div className="flex items-start gap-2.5">
            <div
              className={`w-8 h-8 shrink-0 flex items-center justify-center ${platform === "linkedin" ? "bg-[#0077b5]" : "bg-purple-600"} rounded-lg text-white shadow-xs`}
            >
              <Megaphone className="w-5 h-5" />
            </div>
            <div className="h-8 flex flex-col justify-between">
              <h2 className="text-xs font-bold text-gray-900 uppercase tracking-tight leading-none">
                {t("Plataforma de Veiculação")}
              </h2>
              <span className="text-[10px] text-gray-400 font-medium leading-none">
                {t("Escolha onde seu anúncio será veiculado")}
              </span>
            </div>
          </div>
          <span className="text-[10px] font-bold text-purple-700 bg-purple-50 px-2.5 py-0.5 rounded-full border border-purple-200/60">
            {getPlatformName(platform)}
          </span>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            {
              id: "meta",
              name: "Meta Ads",
              desc: t("FB & Instagram"),
              icon: "meta",
              brandColor: "bg-[#1877f2]",
            },
            {
              id: "google_ads",
              name: "Google Ads",
              desc: t("Busca & YouTube"),
              icon: "google_ads",
              brandColor: "bg-[#db4437]",
            },
            {
              id: "linkedin",
              name: "LinkedIn Ads",
              desc: t("B2B Profissional"),
              icon: "linkedin",
              brandColor: "bg-[#0077b5]",
            },
            {
              id: "tiktok",
              name: "TikTok Ads",
              desc: t("Vídeos Curtos"),
              icon: "tiktok",
              brandColor: "bg-black",
            },
            {
              id: "chatgpt_ads",
              name: "ChatGPT Ads",
              desc: t("Anúncios no ChatGPT"),
              icon: "chatgpt_ads",
              brandColor: "bg-emerald-600",
            },
          ].map((p) => {
            const isSelected = platform === p.id;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => setPlatform(p.id as any)}
                className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left relative overflow-hidden ${
                  isSelected
                    ? p.id === "linkedin"
                      ? "border-[#0077b5] bg-blue-50/40 shadow-xs ring-2 ring-blue-500/10"
                      : "border-purple-600 bg-purple-50/40 shadow-xs ring-2 ring-purple-600/10"
                    : "border-gray-100 bg-gray-50/50 hover:bg-white hover:border-purple-200"
                }`}
              >
                <div
                  className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all shrink-0 ${
                    isSelected
                      ? `${p.brandColor} text-white shadow-xs scale-105`
                      : "bg-white text-gray-400 border border-gray-100"
                  }`}
                >
                  <PlatformIcon platform={p.icon as any} size={22} />
                </div>
                <div className="min-w-0">
                  <div
                    className={`text-xs font-bold truncate leading-tight ${isSelected ? "text-gray-900" : "text-gray-600"}`}
                  >
                    {p.name}
                  </div>
                  <div className="text-[10px] text-gray-400 font-medium truncate mt-0.5">
                    {p.desc}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Tactical Config Card */}
      <div
        className={`bg-white rounded-2xl p-4 md:p-5 border ${platform === "linkedin" ? "border-[#0077b5]/30 shadow-xs" : "border-purple-200/70 shadow-xs"} space-y-4`}
      >
        <div
          className={`flex items-start gap-2.5 border-b pb-3 ${platform === "linkedin" ? "border-blue-100" : "border-purple-100"}`}
        >
          <div
            className={`w-8 h-8 shrink-0 flex items-center justify-center ${platform === "linkedin" ? "bg-[#0077b5]" : "bg-purple-600"} rounded-lg text-white shadow-xs`}
          >
            <Settings2 className="w-5 h-5" />
          </div>
          <div className="h-8 flex flex-col justify-between">
            <h2 className="text-xs font-bold text-gray-900 uppercase tracking-tight leading-none">
              {t("Configurações Gerais & Destino")}
            </h2>
            <span className="text-[10px] text-gray-400 font-medium leading-none">
              {t("Identificação, objetivo e canal de conversão")}
            </span>
          </div>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
            <Input
              label={t("Nome da Campanha")}
              labelRight={
                CAMPAIGN_NAME_LIMITS[platform] ? (
                  <CharCounter
                    current={name.length}
                    hard={CAMPAIGN_NAME_LIMITS[platform]}
                  />
                ) : undefined
              }
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("Ex: Campanha de Leads")}
              inputSize="sm"
              className={`rounded-xl ${platform === "tiktok" ? "md:col-span-2" : ""} ${platform === "linkedin" ? "focus:border-[#0077b5] border-blue-200" : "border-purple-200"}`}
              required
            />

            {platform === "google_ads" ? (
              <Input
                label={t("Nome da Empresa (Google Ads)")}
                labelRight={
                  <CharCounter
                    current={businessName.length}
                    hard={GOOGLE_ADS_BUSINESS_NAME_LIMIT}
                  />
                }
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                placeholder={t("Ex: Minha Empresa Ltda")}
                hint={t(
                  "Exibido nos anúncios do Google. Máx. {max} caracteres.",
                  { max: GOOGLE_ADS_BUSINESS_NAME_LIMIT },
                )}
                inputSize="sm"
                className="rounded-xl border-purple-200"
              />
            ) : platform === "meta" ? (
              <Select
                label={t("Plataforma de Entrega")}
                value={metaPlatform}
                onChange={(val) => setMetaPlatform(val as any)}
                options={[
                  {
                    value: "both",
                    label: t("Ambos (FB + IG) (Recomendado)"),
                    icon: <Layers />,
                  },
                  {
                    value: "facebook",
                    label: t("Somente Facebook"),
                    icon: <Facebook />,
                  },
                  {
                    value: "instagram",
                    label: t("Somente Instagram"),
                    icon: <Instagram />,
                  },
                ]}
                size="sm"
                className="rounded-xl border-purple-200 text-xs font-semibold"
              />
            ) : platform === "linkedin" ? (
              <Select
                label={t("Objetivo no LinkedIn")}
                value={linkedinObjective}
                onChange={(val) => setLinkedinObjective(val as any)}
                options={[
                  {
                    value: "awareness",
                    label: t("Conhecimento da marca"),
                    icon: <Megaphone />,
                  },
                  {
                    value: "traffic",
                    label: t("Visitas ao site"),
                    icon: <MousePointerClick />,
                  },
                  {
                    value: "engagement",
                    label: t("Engajamento"),
                    icon: <Heart />,
                  },
                  {
                    value: "leads",
                    label: t("Geração de leads"),
                    icon: <UserPlus />,
                  },
                  {
                    value: "conversion",
                    label: t("Conversões no site"),
                    icon: <Target />,
                  },
                ]}
                size="sm"
                className="rounded-xl border-[#0077b5]/30 text-xs font-semibold"
              />
            ) : platform === "chatgpt_ads" ? (
              <Select
                label={t("Objetivo")}
                value={chatgptObjective}
                onChange={(val) => setChatgptObjective(val as any)}
                options={[
                  {
                    value: "clicks",
                    label: t("Cliques (lance fixo)"),
                    icon: <MousePointerClick />,
                  },
                  {
                    value: "conversions",
                    label: t("Conversões (otimizado)"),
                    icon: <Target />,
                  },
                ]}
                size="sm"
                className="rounded-xl border-emerald-200 text-xs font-semibold"
              />
            ) : null}
          </div>

          {platform !== "chatgpt_ads" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
              <Select
                label={t("Destino / Ação Principal")}
                hint={t(
                  "Muda automaticamente o Objetivo de Desempenho (e, no LinkedIn, o Objetivo) para o mais adequado a essa ação.",
                )}
                value={destinationType}
                onChange={(val) => {
                  setDestinationType(val as any);
                  if (platform === "linkedin" && val === "form") {
                    setLinkedinObjective("leads");
                  } else if (
                    platform === "linkedin" &&
                    val !== "form" &&
                    linkedinObjective === "leads"
                  ) {
                    setLinkedinObjective("traffic");
                  }
                  if (val === "calls") setPerformanceGoal("QUALITY_CALL");
                  else if (
                    ["whatsapp", "messenger", "instagram_direct"].includes(val)
                  )
                    setPerformanceGoal("CONVERSATIONS");
                  else setPerformanceGoal("LINK_CLICKS");
                }}
                options={[
                  {
                    value: "website",
                    label: "Tráfego para Site (URL)",
                    icon: <Globe />,
                  },
                  {
                    value: "whatsapp",
                    label: "Conversão (WhatsApp)",
                    icon: <MessageCircle />,
                  },
                  {
                    value: "form",
                    label: "Geração de Leads (Formulário)",
                    icon: <ClipboardList />,
                  },
                  ...(platform === "meta"
                    ? [
                        {
                          value: "messenger",
                          label: "Mensagens (Messenger)",
                          icon: <MessageCircle />,
                        },
                        {
                          value: "instagram_direct",
                          label: "Mensagens (Instagram Direct)",
                          icon: <MessageCircle />,
                        },
                        {
                          value: "calls",
                          label: "Chamadas (Ligar agora)",
                          icon: <PhoneCall />,
                        },
                      ]
                    : []),
                ]}
                size="sm"
                className={`rounded-xl text-xs font-semibold ${platform !== "google_ads" ? "md:col-span-2" : ""} ${platform === "linkedin" ? "border-[#0077b5]/30" : "border-purple-200"}`}
              />

              {platform === "google_ads" && (
                <Select
                  label={t("Tipo de Campanha Google")}
                  value={googleCampaignType}
                  onChange={(val) => setGoogleCampaignType(val as any)}
                  options={[
                    {
                      value: "search",
                      label: t("Rede de Pesquisa"),
                      icon: <Search />,
                    },
                    {
                      value: "display",
                      label: t("Rede de Display"),
                      icon: <ImageIcon />,
                    },
                    {
                      value: "video",
                      label: t("YouTube Video"),
                      icon: <Video />,
                    },
                    {
                      value: "performance_max",
                      label: t("Performance Max (IA)"),
                      icon: <Sparkles />,
                    },
                  ]}
                  size="sm"
                  className="rounded-xl border-purple-200 text-xs font-semibold"
                />
              )}
            </div>
          )}

          {/* ChatGPT Ads: sem opções de destino (sempre tráfego para URL via chat_card).
              A URL em si é preenchida na Etapa 2 (Conteúdo), junto com o resto do
              criativo — igual ao fluxo de Meta/LinkedIn/Google, sem duplicar o campo aqui. */}
          {platform === "chatgpt_ads" && (
            <div className="pt-3.5 border-t border-emerald-100/70">
              <p className="text-[10px] text-gray-400 leading-relaxed">
                {t(
                  "O anúncio será exibido como um card dentro da conversa do ChatGPT, levando o usuário direto para a URL de destino informada na próxima etapa.",
                )}
              </p>
            </div>
          )}

          {/* Performance Goal for Meta — Streamlined Section (no card-inside-card) */}
          {platform === "meta" && destinationType !== "form" && (
            <div className="pt-3.5 border-t border-purple-100/70 space-y-2.5">
              <div className="flex items-center gap-2">
                <Phone className="w-3.5 h-3.5 text-purple-600" />
                <span className="text-[10px] font-bold text-gray-700 uppercase tracking-wider">
                  {t("Objetivo de Desempenho")}
                </span>
                <span className="text-[10px] text-gray-400 ml-auto">
                  {t("Como medir o sucesso dos anúncios")}
                </span>
              </div>
              <div className="space-y-2">
                {PERFORMANCE_GOAL_GROUPS.map((group) => {
                  const visibleGoals = group.goals.filter((g) => {
                    if (g === "QUALITY_CALL")
                      return destinationType === "calls";
                    if (g === "CONVERSATIONS")
                      return [
                        "whatsapp",
                        "messenger",
                        "instagram_direct",
                      ].includes(destinationType);
                    return destinationType !== "calls";
                  });
                  if (visibleGoals.length === 0) return null;
                  return (
                    <div key={group.label} className="space-y-1">
                      <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">
                        {t(group.label)}
                      </span>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        {visibleGoals.map((goal) => (
                          <label
                            key={goal}
                            className={`flex items-center gap-2 p-2.5 rounded-xl border text-xs cursor-pointer transition-all ${
                              performanceGoal === goal
                                ? "border-purple-600 bg-purple-50/50 text-purple-900 font-bold shadow-xs"
                                : "border-gray-200 bg-gray-50/40 hover:bg-white hover:border-purple-200 text-gray-700"
                            }`}
                          >
                            <input
                              type="radio"
                              name="performanceGoal"
                              value={goal}
                              checked={performanceGoal === goal}
                              onChange={() => setPerformanceGoal(goal)}
                              className="accent-purple-600 w-3.5 h-3.5"
                            />
                            <span className="truncate leading-tight">
                              {t(PERFORMANCE_GOAL_LABELS[goal])}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Calls Target */}
          {destinationType === "calls" && (
            <div className="pt-3 border-t border-purple-100/70">
              <Input
                label={t("Número de Telefone (com DDI)")}
                hint={t("Número com DDI + DDD para o botão 'Ligar agora'.")}
                value={whatsappPhone}
                onChange={(e) => setWhatsappPhone(e.target.value)}
                placeholder="Ex: 5511999999999"
                inputSize="sm"
                className="rounded-xl border-purple-200"
                required
              />
            </div>
          )}

          {/* WhatsApp Destination Settings */}
          {destinationType === "whatsapp" && (
            <div className="p-3.5 bg-emerald-50/30 rounded-xl border border-emerald-200/70 space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <span className="text-xs font-bold text-emerald-900 uppercase tracking-tight">
                  {t("Configuração do WhatsApp")}
                </span>
                <div className="flex bg-white rounded-lg p-0.5 border border-emerald-200">
                  <button
                    type="button"
                    onClick={() => setWhatsappOfficial(false)}
                    className={`py-1.5 px-3 rounded-md text-xs font-bold transition-all ${
                      !whatsappOfficial
                        ? "bg-emerald-600 text-white shadow-xs"
                        : "text-gray-500 hover:text-emerald-700"
                    }`}
                  >
                    {t("Link Direto (wa.me)")}
                  </button>
                  <button
                    type="button"
                    onClick={() => setWhatsappOfficial(true)}
                    className={`py-1.5 px-3 rounded-md text-xs font-bold transition-all ${
                      whatsappOfficial
                        ? "bg-emerald-600 text-white shadow-xs"
                        : "text-gray-500 hover:text-emerald-700"
                    }`}
                  >
                    {t("WhatsApp Oficial (Meta BM)")}
                  </button>
                </div>
              </div>

              <div className="p-2.5 bg-white rounded-lg border border-emerald-100 flex items-center gap-2.5">
                <div
                  className={`w-6 h-6 rounded-full flex items-center justify-center text-white font-black text-[9px] shadow-xs shrink-0 ${
                    whatsappOfficial ? "bg-blue-500" : "bg-emerald-500"
                  }`}
                >
                  {whatsappOfficial ? "API" : "WA"}
                </div>
                <p className="text-[10px] font-medium text-gray-600 leading-snug">
                  {whatsappOfficial
                    ? t(
                        "O anúncio utilizará o botão nativo do WhatsApp e objetivo de Mensagens.",
                      )
                    : t(
                        "O link wa.me será gerado automaticamente com base nos valores informados acima.",
                      )}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input
                  label={t("Número do WhatsApp (DDI + DDD + Número)")}
                  value={whatsappPhone}
                  onChange={(e) => setWhatsappPhone(e.target.value)}
                  placeholder="Ex: 5511999999999"
                  inputSize="sm"
                  className="rounded-xl border-emerald-200"
                  required
                />
                {!whatsappOfficial && (
                  <Input
                    label={t("Mensagem Pré-definida (Opcional)")}
                    value={whatsappPrefill}
                    onChange={(e) => setWhatsappPrefill(e.target.value)}
                    placeholder={t("Olá! Quero mais informações...")}
                    inputSize="sm"
                    className="rounded-xl border-emerald-200"
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Step1_GeneralConfig;
